import {
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canManageTeam } from '@finvox/shared/org';
import { parseMoneyInput } from '@finvox/shared/money';
import {
  filterAccountCode,
  filterArabicName,
  filterLatinName,
  isValidArabicName,
  isValidLatinName
} from '@finvox/shared/text-locale';
import { validateJournalBalance } from '@finvox/shared/journal';
import { getFirestoreDb } from '@/lib/firebase';
import { useOrg } from '@/contexts/OrgContext';
import { useToast } from '@/contexts/ToastContext';
import { actionPillVariants } from '@/components/ui/button-variants';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LocaleNameInput } from '@/components/ui/LocaleNameInput';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Select } from '@/components/ui/Select';
import { PageContent } from '@/components/layout/PageContent';
import { Tabs } from '@/components/ui/Tabs';
import { apiPost } from '@/lib/api';
import { mapJournalBalanceError, toastError } from '@/lib/toastErrors';
import { isWorkerFirebaseReady } from '@/lib/workerApi';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { PaginatedTable } from '@/components/ui/PaginatedTable';

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense', 'cogs'] as const;
type AccountType = (typeof ACCOUNT_TYPES)[number];

type Account = {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  type: string;
  active: boolean;
};

function normKey(s: string): string {
  return s.trim().toLowerCase();
}

type DuplicateKind = 'code' | 'name' | 'nameAr' | 'namesMustDiffer';

function findDuplicate(
  accounts: Account[],
  payload: { code: string; name: string; nameAr: string },
  excludeId?: string
): DuplicateKind | null {
  const c = normKey(payload.code);
  const n = normKey(payload.name);
  const na = normKey(payload.nameAr);
  if (c && accounts.some((a) => a.id !== excludeId && normKey(a.code) === c)) return 'code';
  if (n && accounts.some((a) => a.id !== excludeId && normKey(a.name) === n)) return 'name';
  if (na && accounts.some((a) => a.id !== excludeId && normKey(a.nameAr) === na)) return 'nameAr';
  if (na && n && na === n) return 'namesMustDiffer';
  return null;
}

export function CoaPage() {
  const { t, i18n } = useTranslation();
  const { orgId, org, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const [usedAccountIds, setUsedAccountIds] = useState<Set<string>>(new Set());
  const [opening, setOpening] = useState<Record<string, { debit: string; credit: string }>>({});
  const [tab, setTab] = useState('accounts');
  const [showInactive, setShowInactive] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [type, setType] = useState<AccountType>('expense');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editName, setEditName] = useState('');
  const [editNameAr, setEditNameAr] = useState('');

  const canEdit = org && canManageTeam(org.role);

  const typeOptions = useMemo(
    () =>
      ACCOUNT_TYPES.map((value) => ({
        value,
        label: t(`coa.types.${value}`)
      })),
    [t]
  );

  const accountConstraints = useMemo(
    () => (showInactive ? [] : [where('active', '==', true)]),
    [showInactive]
  );

  const mapAccount = useCallback(
    (id: string, data: Record<string, unknown>) =>
      ({
        id,
        code: String(data.code ?? ''),
        name: String(data.name ?? ''),
        nameAr: String(data.nameAr ?? ''),
        type: String(data.type ?? 'expense'),
        active: data.active !== false
      }) as Account,
    []
  );

  const accountsPage = useFirestorePageQuery<Account>({
    orgId,
    collectionPath: 'accounts',
    orderByField: 'code',
    orderDirection: 'asc',
    mapDoc: mapAccount,
    constraints: accountConstraints,
    enabled: tab === 'accounts'
  });

  const openingPage = useFirestorePageQuery<Account>({
    orgId,
    collectionPath: 'accounts',
    orderByField: 'code',
    orderDirection: 'asc',
    mapDoc: mapAccount,
    constraints: [where('active', '==', true)],
    enabled: tab === 'opening'
  });

  useEffect(() => {
    if (!orgId) return;
    void apiPost<{ accountIds: string[] }>('/v1/accounts/used-ids', { orgId }).then((res) => {
      setUsedAccountIds(new Set(res.accountIds));
    });
  }, [orgId]);

  function validateNames(
    latin: string,
    arabic: string
  ): 'invalidLatinName' | 'invalidArabicName' | null {
    if (!isValidLatinName(latin)) return 'invalidLatinName';
    if (arabic.trim() && !isValidArabicName(arabic)) return 'invalidArabicName';
    return null;
  }

  function toastDuplicate(kind: DuplicateKind) {
    const key =
      kind === 'code'
        ? 'coa.duplicateCode'
        : kind === 'name'
          ? 'coa.duplicateName'
          : kind === 'nameAr'
            ? 'coa.duplicateNameAr'
            : 'coa.namesMustDiffer';
    toast.error(t(key), 'coa-dup');
  }

  async function addAccount() {
    if (!orgId || !online || !code.trim() || !name.trim()) return;
    const payload = {
      code: filterAccountCode(code).trim(),
      name: filterLatinName(name).trim(),
      nameAr: filterArabicName(nameAr).trim()
    };
    const nameErr = validateNames(payload.name, payload.nameAr);
    if (nameErr) {
      toast.error(t(`coa.${nameErr}`), 'coa-val');
      return;
    }
    const dup = findDuplicate(visibleAccounts, payload);
    if (dup) {
      toastDuplicate(dup);
      return;
    }
    try {
      const db = getFirestoreDb();
      await setDoc(doc(collection(db, 'organizations', orgId, 'accounts')), {
        code: payload.code,
        name: payload.name,
        nameAr: payload.nameAr,
        type,
        active: true,
        createdAt: serverTimestamp()
      });
      setCode('');
      setName('');
      setNameAr('');
      setType('expense');
      accountsPage.refresh();
      toast.success(t('coa.saved'), 'coa-add');
    } catch (e) {
      toastError(toast, e, 'coa-add');
    }
  }

  async function postOpening() {
    if (!orgId || !currentPeriodId || !online) return;
    if (!(await isWorkerFirebaseReady())) {
      toast.error(t('errors.workerUnavailable'), 'coa-opening');
      return;
    }
    const lines = Object.entries(opening)
      .map(([accountId, o]) => {
        const debitRaw = o.debit.trim();
        const creditRaw = o.credit.trim();
        if (!debitRaw && !creditRaw) return null;
        const debit = debitRaw ? parseMoneyInput(debitRaw) : '0.00';
        const credit = creditRaw ? parseMoneyInput(creditRaw) : '0.00';
        if (debit === '0.00' && credit === '0.00') return null;
        return { accountId, debit, credit };
      })
      .filter(Boolean) as { accountId: string; debit: string; credit: string }[];

    if (!lines.length) return;

    const balance = validateJournalBalance(
      lines.map((l) => ({ accountId: l.accountId, debit: l.debit, credit: l.credit }))
    );
    if (!balance.valid) {
      toast.error(mapJournalBalanceError(balance.error), 'coa-opening-balance');
      return;
    }

    try {
      await apiPost('/v1/opening-balances', { orgId, periodId: currentPeriodId, lines });
      toast.success(t('coa.openingPosted'), 'coa-opening');
      setOpening({});
      setTab('accounts');
    } catch (e) {
      toastError(toast, e, 'coa-opening');
    }
  }

  async function saveEdit(accountId: string) {
    if (!orgId || !online) return;
    const payload = {
      code: filterAccountCode(editCode).trim(),
      name: filterLatinName(editName).trim(),
      nameAr: filterArabicName(editNameAr).trim()
    };
    const nameErr = validateNames(payload.name, payload.nameAr);
    if (nameErr) {
      toast.error(t(`coa.${nameErr}`), 'coa-val');
      return;
    }
    const dup = findDuplicate(visibleAccounts, payload, accountId);
    if (dup) {
      toastDuplicate(dup);
      return;
    }
    try {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'accounts', accountId),
        {
          code: payload.code,
          name: payload.name,
          nameAr: payload.nameAr || payload.name
        },
        { merge: true }
      );
      setEditingId(null);
      accountsPage.refresh();
      toast.success(t('coa.saved'), 'coa-edit');
    } catch (e) {
      toastError(toast, e, 'coa-edit');
    }
  }

  async function deactivateAccount(accountId: string) {
    if (!orgId || !online) return;
    if (!window.confirm(t('coa.confirmDeactivate'))) return;
    try {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'accounts', accountId),
        { active: false },
        { merge: true }
      );
      accountsPage.refresh();
      toast.success(t('coa.deactivated'), 'coa-deactivate');
    } catch (e) {
      toastError(toast, e, 'coa-deactivate');
    }
  }

  async function activateAccount(accountId: string) {
    if (!orgId || !online) return;
    try {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'accounts', accountId),
        { active: true },
        { merge: true }
      );
      accountsPage.refresh();
      toast.success(t('coa.activated'), 'coa-activate');
    } catch (e) {
      toastError(toast, e, 'coa-activate');
    }
  }

  async function deleteAccount(accountId: string) {
    if (!orgId || !online) return;
    if (usedAccountIds.has(accountId)) {
      toast.error(t('coa.cannotDeleteUsed'), 'coa-delete');
      return;
    }
    if (!window.confirm(t('coa.confirmDelete'))) return;
    try {
      await deleteDoc(doc(getFirestoreDb(), 'organizations', orgId, 'accounts', accountId));
      accountsPage.refresh();
      toast.success(t('coa.deleted'), 'coa-delete');
      if (editingId === accountId) setEditingId(null);
    } catch (e) {
      toastError(toast, e, 'coa-delete');
    }
  }

  const displayName = (a: Account) =>
    i18n.language === 'ar' ? a.nameAr || a.name : a.name;

  const visibleAccounts = accountsPage.rows;

  const accountsPanel = (
    <>
      {canEdit && (
        <div className="grid min-w-0 gap-3 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            placeholder={t('coa.code')}
            value={code}
            onChange={(e) => setCode(filterAccountCode(e.target.value))}
            disabled={!online}
          />
          <LocaleNameInput
            locale="en"
            placeholder={t('coa.name')}
            value={name}
            onValueChange={setName}
            disabled={!online}
          />
          <LocaleNameInput
            locale="ar"
            placeholder={t('coa.nameAr')}
            value={nameAr}
            onValueChange={setNameAr}
            disabled={!online}
          />
          <Select
            className="w-full"
            value={type}
            disabled={!online}
            onValueChange={(v) => setType(v as AccountType)}
            options={typeOptions}
            aria-label={t('coa.type')}
          />
          <Button
            className="sm:col-span-2 lg:col-span-5"
            disabled={!online}
            onClick={() => void addAccount()}
          >
            {t('coa.addAccount')}
          </Button>
        </div>
      )}

      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        {t('coa.showInactive')}
      </label>

      <PaginatedTable
        className="mt-4"
        loading={accountsPage.loading}
        pageIndex={accountsPage.pageIndex}
        hasNext={accountsPage.hasNext}
        hasPrev={accountsPage.hasPrev}
        onNext={accountsPage.nextPage}
        onPrev={accountsPage.prevPage}
        pageSize={accountsPage.pageSize}
        onPageSizeChange={accountsPage.setPageSize}
      >
        <table className="w-full min-w-[36rem] table-fixed text-sm">
          <thead className="bg-[var(--fv-bg)]">
            <tr>
              <th className="w-[6rem] p-3 text-start">{t('coa.code')}</th>
              <th className="p-3 text-start">{t('coa.name')}</th>
              <th className="w-[9rem] p-3 text-start">{t('coa.type')}</th>
              {canEdit && <th className="w-[14rem] p-3 text-end">{t('coa.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {visibleAccounts.map((a) => {
              const isEditing = editingId === a.id;
              const typeLocked = usedAccountIds.has(a.id);
              return (
                <tr
                  key={a.id}
                  className={`border-t border-[var(--fv-border)] ${!a.active ? 'opacity-50' : ''}`}
                >
                  <td className="p-3 font-mono">
                    {isEditing ? (
                      <Input
                        className="w-full font-mono text-xs"
                        value={editCode}
                        onChange={(e) => setEditCode(filterAccountCode(e.target.value))}
                        disabled={!online}
                      />
                    ) : (
                      a.code
                    )}
                  </td>
                  <td className="min-w-0 p-3">
                    {isEditing ? (
                      <div className="flex min-w-0 flex-col gap-1">
                        <LocaleNameInput
                          locale="en"
                          placeholder={t('coa.name')}
                          value={editName}
                          onValueChange={setEditName}
                          disabled={!online}
                        />
                        <LocaleNameInput
                          locale="ar"
                          placeholder={t('coa.nameAr')}
                          value={editNameAr}
                          onValueChange={setEditNameAr}
                          disabled={!online}
                        />
                      </div>
                    ) : (
                      <span className="block truncate" title={displayName(a)}>
                        {displayName(a)}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="block truncate">
                      {t(`coa.types.${a.type}`, { defaultValue: a.type })}
                    </span>
                    {typeLocked && (
                      <span className="text-xs text-[var(--fv-text-muted)]">
                        ({t('coa.typeLocked')})
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="p-3 text-end">
                      {isEditing ? (
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button
                            type="button"
                            className={actionPillVariants({ variant: 'primary' })}
                            onClick={() => void saveEdit(a.id)}
                          >
                            {t('common.save')}
                          </button>
                          <button
                            type="button"
                            className={actionPillVariants({ variant: 'neutral' })}
                            onClick={() => setEditingId(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button
                            type="button"
                            className={actionPillVariants({ variant: 'soft' })}
                            disabled={!online}
                            onClick={() => {
                              setEditingId(a.id);
                              setEditCode(a.code);
                              setEditName(a.name);
                              setEditNameAr(
                                a.nameAr && a.nameAr !== a.name ? a.nameAr : ''
                              );
                            }}
                          >
                            {t('coa.edit')}
                          </button>
                          {a.active ? (
                            <button
                              type="button"
                              className={actionPillVariants({ variant: 'danger' })}
                              disabled={!online}
                              onClick={() => void deactivateAccount(a.id)}
                            >
                              {t('coa.deactivate')}
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className={actionPillVariants({ variant: 'success' })}
                                disabled={!online}
                                onClick={() => void activateAccount(a.id)}
                              >
                                {t('coa.activate')}
                              </button>
                              <button
                                type="button"
                                className={actionPillVariants({ variant: 'danger' })}
                                disabled={!online}
                                onClick={() => void deleteAccount(a.id)}
                              >
                                {t('coa.delete')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </PaginatedTable>
    </>
  );

  const openingPanel = (
    <div className="min-w-0 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4">
      <p className="text-sm text-[var(--fv-text-muted)]">{t('coa.openingHint')}</p>
      <PaginatedTable
        className="mt-3"
        loading={openingPage.loading}
        pageIndex={openingPage.pageIndex}
        hasNext={openingPage.hasNext}
        hasPrev={openingPage.hasPrev}
        onNext={openingPage.nextPage}
        onPrev={openingPage.prevPage}
        pageSize={openingPage.pageSize}
        onPageSizeChange={openingPage.setPageSize}
      >
        <table className="w-full min-w-[36rem] table-fixed text-sm">
          <thead className="bg-[var(--fv-bg)]">
            <tr>
              <th className="w-[6rem] p-3 text-start">{t('coa.code')}</th>
              <th className="p-3 text-start">{t('coa.name')}</th>
              <th className="w-[8rem] p-3 text-end">{t('coa.debit')}</th>
              <th className="w-[8rem] p-3 text-end">{t('coa.credit')}</th>
            </tr>
          </thead>
          <tbody>
            {openingPage.rows.map((a) => (
              <tr key={a.id} className="border-t border-[var(--fv-border)]">
                <td className="p-3 font-mono text-xs">{a.code}</td>
                <td className="min-w-0 p-3">
                  <span className="block truncate" title={displayName(a)}>
                    {displayName(a)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex justify-end">
                    <MoneyInput
                      allowEmpty
                      className="w-28"
                      disabled={!online}
                      placeholder=""
                      value={opening[a.id]?.debit ?? ''}
                      onValueChange={(debit) =>
                        setOpening((o) => ({
                          ...o,
                          [a.id]: { debit, credit: '' }
                        }))
                      }
                    />
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex justify-end">
                    <MoneyInput
                      allowEmpty
                      className="w-28"
                      disabled={!online}
                      placeholder=""
                      value={opening[a.id]?.credit ?? ''}
                      onValueChange={(credit) =>
                        setOpening((o) => ({
                          ...o,
                          [a.id]: { debit: '', credit }
                        }))
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </PaginatedTable>
      <Button className="mt-3" disabled={!online} onClick={() => void postOpening()}>
        {t('coa.postOpening')}
      </Button>
    </div>
  );

  return (
    <PageContent className="min-w-0">
      <h1 className="text-2xl font-bold">{t('nav.coa')}</h1>
      <Tabs
        className="mt-6 w-full min-w-0"
        tabListClassName="max-w-lg"
        activeId={tab}
        onChange={setTab}
        tabs={[
          { id: 'accounts', label: t('coa.tabAccounts'), panel: accountsPanel },
          { id: 'opening', label: t('coa.tabOpening'), panel: openingPanel }
        ]}
      />
    </PageContent>
  );
}
